import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, EmptyState } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

export function AdminConfigStudio() {
  const [tab, setTab] = useState("modules");
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Config studio" testId="admin-config-header">
        <div className="flex gap-1">
          {["modules","pricing","faqs","wholesale"].map((t) => (
            <button key={t} className={`dt6-btn text-xs ${tab===t ? "dt6-btn-primary" : ""}`} onClick={() => setTab(t)} data-testid={`config-tab-${t}`}>{t}</button>
          ))}
        </div>
      </PortalHeader>
      <PortalBody>
        {tab === "modules" && <ModulesEditor />}
        {tab === "pricing" && <PricingEditor />}
        {tab === "faqs" && <FaqsEditor />}
        {tab === "wholesale" && <WholesaleEditor />}
      </PortalBody>
    </>
  );
}

function ModulesEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/config/modules").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing };
      payload.deliverables = typeof payload.deliverables === "string" ? payload.deliverables.split("\n").filter(Boolean) : payload.deliverables;
      payload.use_cases = typeof payload.use_cases === "string" ? payload.use_cases.split("\n").filter(Boolean) : payload.use_cases;
      payload.works_with = typeof payload.works_with === "string" ? payload.works_with.split(",").map(s=>s.trim()).filter(Boolean) : payload.works_with;
      await api.put(`/admin/config/modules/${payload.code}`, payload);
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="config-modules">
      {rows.map((m) => (
        <div key={m.code} className="dt6-card p-4 flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-xs text-[color:var(--dt6-muted)]">{m.code}</div>
            <div className="font-heading text-lg font-bold">{m.name} — <span className="text-[color:var(--dt6-muted)] font-normal">{m.tagline}</span></div>
            <div className="text-xs text-[color:var(--dt6-muted)] mt-1">{(m.deliverables || []).length} deliverables · {(m.use_cases || []).length} use cases</div>
          </div>
          <button className="dt6-btn text-xs" onClick={() => setEditing({
            ...m,
            deliverables: (m.deliverables || []).join("\n"),
            use_cases: (m.use_cases || []).join("\n"),
            works_with: (m.works_with || []).join(", "),
          })} data-testid={`config-mod-edit-${m.code}`}>Edit</button>
        </div>
      ))}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="dt6-card p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()} style={{ background: "var(--dt6-surface)" }} data-testid="config-mod-editor">
            <div className="dt6-chip mb-3">EDIT {editing.code}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="dt6-label">Name</label><input className="dt6-input" value={editing.name} onChange={(e)=>setEditing({...editing,name:e.target.value})} data-testid="cmod-name" /></div>
              <div><label className="dt6-label">Order</label><input className="dt6-input" type="number" value={editing.display_order} onChange={(e)=>setEditing({...editing,display_order:parseInt(e.target.value)})} data-testid="cmod-order" /></div>
              <div className="col-span-2"><label className="dt6-label">Tagline</label><input className="dt6-input" value={editing.tagline} onChange={(e)=>setEditing({...editing,tagline:e.target.value})} data-testid="cmod-tagline" /></div>
              <div className="col-span-2"><label className="dt6-label">What it does</label><textarea className="dt6-textarea" rows={3} value={editing.what_it_does} onChange={(e)=>setEditing({...editing,what_it_does:e.target.value})} data-testid="cmod-desc" /></div>
              <div className="col-span-2"><label className="dt6-label">Deliverables (one per line)</label><textarea className="dt6-textarea" rows={4} value={editing.deliverables} onChange={(e)=>setEditing({...editing,deliverables:e.target.value})} data-testid="cmod-deliv" /></div>
              <div className="col-span-2"><label className="dt6-label">Use cases (one per line)</label><textarea className="dt6-textarea" rows={4} value={editing.use_cases} onChange={(e)=>setEditing({...editing,use_cases:e.target.value})} data-testid="cmod-usecase" /></div>
              <div className="col-span-2"><label className="dt6-label">Works with (comma-separated codes)</label><input className="dt6-input" value={editing.works_with} onChange={(e)=>setEditing({...editing,works_with:e.target.value})} data-testid="cmod-works" /></div>
            </div>
            {err && <div className="mt-3 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button className="dt6-btn text-xs" onClick={() => setEditing(null)} data-testid="cmod-cancel">Cancel</button>
              <button className="dt6-btn dt6-btn-primary text-xs" onClick={save} data-testid="cmod-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PricingEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/config/pricing").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing, features: typeof editing.features === "string" ? editing.features.split("\n").filter(Boolean) : editing.features };
      await api.put(`/admin/config/pricing/${payload.slug}`, payload);
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="config-pricing">
      {rows.map((t) => (
        <div key={t.slug} className="dt6-card p-4 flex items-center justify-between">
          <div>
            <div className="font-heading text-lg font-bold">{t.name} <span className="font-mono text-xs text-[color:var(--dt6-muted)] ml-2">{t.price_display}</span></div>
            <div className="text-xs text-[color:var(--dt6-muted)] mt-1">{(t.features || []).length} features {t.highlight && "· HIGHLIGHT"}</div>
          </div>
          <button className="dt6-btn text-xs" onClick={() => setEditing({ ...t, features: (t.features || []).join("\n") })} data-testid={`config-pricing-edit-${t.slug}`}>Edit</button>
        </div>
      ))}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setEditing(null)}>
          <div className="dt6-card p-6 w-full max-w-xl" onClick={(e)=>e.stopPropagation()} style={{ background: "var(--dt6-surface)" }} data-testid="config-pricing-editor">
            <div className="dt6-chip mb-3">EDIT {editing.slug}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="dt6-label">Name</label><input className="dt6-input" value={editing.name} onChange={(e)=>setEditing({...editing,name:e.target.value})} data-testid="cpr-name" /></div>
              <div><label className="dt6-label">Price display</label><input className="dt6-input" value={editing.price_display} onChange={(e)=>setEditing({...editing,price_display:e.target.value})} data-testid="cpr-price" /></div>
              <div className="col-span-2"><label className="dt6-label">Audience</label><input className="dt6-input" value={editing.audience} onChange={(e)=>setEditing({...editing,audience:e.target.value})} data-testid="cpr-audience" /></div>
              <div className="col-span-2"><label className="dt6-label">Features (one per line)</label><textarea className="dt6-textarea" rows={5} value={editing.features} onChange={(e)=>setEditing({...editing,features:e.target.value})} data-testid="cpr-features" /></div>
              <div><label className="dt6-label">CTA label</label><input className="dt6-input" value={editing.cta_label} onChange={(e)=>setEditing({...editing,cta_label:e.target.value})} data-testid="cpr-cta" /></div>
              <div><label className="dt6-label">Order</label><input className="dt6-input" type="number" value={editing.display_order} onChange={(e)=>setEditing({...editing,display_order:parseInt(e.target.value)})} data-testid="cpr-order" /></div>
              <div className="col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.highlight} onChange={(e)=>setEditing({...editing,highlight:e.target.checked})} data-testid="cpr-highlight" /> Highlight tier
              </div>
            </div>
            {err && <div className="mt-3 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button className="dt6-btn text-xs" onClick={()=>setEditing(null)} data-testid="cpr-cancel">Cancel</button>
              <button className="dt6-btn dt6-btn-primary text-xs" onClick={save} data-testid="cpr-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqsEditor() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState({ question: "", answer: "", display_order: 100 });
  const [err, setErr] = useState("");
  const load = () => api.get("/config/faqs").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setErr("");
    try {
      await api.post("/admin/config/faqs", q);
      setQ({ question: "", answer: "", display_order: 100 });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const del = async (id) => {
    await api.delete(`/admin/config/faqs/${id}`);
    await load();
  };
  return (
    <div className="space-y-4" data-testid="config-faqs">
      <div className="dt6-card p-4">
        <div className="dt6-chip mb-3">+ ADD FAQ</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="dt6-input md:col-span-1" placeholder="question" value={q.question} onChange={(e)=>setQ({...q,question:e.target.value})} data-testid="cfaq-q" />
          <input className="dt6-input md:col-span-2" placeholder="answer" value={q.answer} onChange={(e)=>setQ({...q,answer:e.target.value})} data-testid="cfaq-a" />
        </div>
        {err && <div className="mt-2 text-sm text-[color:var(--dt6-danger)]">{err}</div>}
        <div className="mt-3 flex justify-end">
          <button className="dt6-btn dt6-btn-primary text-xs" onClick={create} disabled={!q.question || !q.answer} data-testid="cfaq-add"><Plus size={12} /> Add FAQ</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="cfaq-empty">NO FAQS YET.</EmptyState>
      ) : rows.map((r) => (
        <div key={r.id} className="dt6-card p-4 flex items-start justify-between gap-4" data-testid={`cfaq-row-${r.id}`}>
          <div className="flex-1">
            <div className="font-medium">{r.question}</div>
            <div className="text-sm text-[color:var(--dt6-muted)]">{r.answer}</div>
          </div>
          <button className="dt6-btn text-xs" onClick={() => del(r.id)} data-testid={`cfaq-del-${r.id}`}><Trash2 size={12} /></button>
        </div>
      ))}
    </div>
  );
}

function WholesaleEditor() {
  const [rows, setRows] = useState([]);
  const [row, setRow] = useState({ service: "", module_code: "D1", wholesale_price: "", retail_price: "", display_order: 100 });
  const load = () => api.get("/config/wholesale").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { await api.post("/admin/config/wholesale", row); setRow({ service: "", module_code: "D1", wholesale_price: "", retail_price: "", display_order: 100 }); await load(); };
  const del = async (id) => { await api.delete(`/admin/config/wholesale/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="config-wholesale">
      <div className="dt6-card p-4">
        <div className="dt6-chip mb-3">+ ADD WHOLESALE ROW</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="dt6-input" placeholder="service" value={row.service} onChange={(e)=>setRow({...row,service:e.target.value})} data-testid="cwh-service" />
          <select className="dt6-select" value={row.module_code} onChange={(e)=>setRow({...row,module_code:e.target.value})} data-testid="cwh-module">
            {["D1","D2","D3","D4","D5","D6"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="dt6-input" placeholder="wholesale price" value={row.wholesale_price} onChange={(e)=>setRow({...row,wholesale_price:e.target.value})} data-testid="cwh-wholesale" />
          <input className="dt6-input" placeholder="retail price" value={row.retail_price} onChange={(e)=>setRow({...row,retail_price:e.target.value})} data-testid="cwh-retail" />
        </div>
        <div className="mt-3 flex justify-end">
          <button className="dt6-btn dt6-btn-primary text-xs" onClick={create} disabled={!row.service} data-testid="cwh-add"><Plus size={12} /> Add row</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="cwh-empty">NO WHOLESALE ROWS.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Service</th><th>Module</th><th>Wholesale</th><th>Retail</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`cwh-row-${r.id}`}>
                  <td>{r.service}</td>
                  <td><span className="font-mono">{r.module_code}</span></td>
                  <td className="font-mono">{r.wholesale_price}</td>
                  <td className="font-mono text-[color:var(--dt6-muted)]">{r.retail_price}</td>
                  <td><button className="dt6-btn text-xs" onClick={() => del(r.id)} data-testid={`cwh-del-${r.id}`}><Trash2 size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
